import { CloseCircleOutlined, EllipsisOutlined } from '@ant-design/icons';
import { Dropdown, MenuProps, Spin, Table, Tag, TagProps, Tooltip } from 'antd';
import { format } from 'date-fns';
import { type Moment } from 'moment';

const formatDisplayDateTime = (
  dateValue: Moment | undefined | null
): string => {
  if (!dateValue) {
    return '';
  }
  return format(dateValue.toDate(), 'yyyy-MM-dd HH:mm');
};

import {
  AssessmentIndexEntry,
  AssessmentIndexStatusType
} from 'lib/documents/patient-assessment/patient-assessment';
import { useAssessmentAPI } from 'lib/hooks/tanstack/use-assessmentAPI';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useErrorListContext } from 'lib/utils/useErrorList';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { theme } from 'styles/colors';
import { usePatientInfoContext } from '../../../../patient/_utils/patient-info/hook-context-patientinfo';

const STATUS_TO_TAG_MAP: Record<
  AssessmentIndexStatusType,
  { tooltip: string; title: string; color?: TagProps['color'] }
> = {
  started: {
    title: 'Started',
    tooltip: 'The Test-Taker has started their assessment.',
    color: 'processing'
  },
  submitted: {
    title: 'Submitted',
    tooltip:
      'The Test-Taker has submitted their assessment and it is being processed',
    color: theme.primary
  },
  completed: {
    title: 'Completed',
    tooltip: 'The Test-Taker has completed their assessment',
    color: theme.primary
  },
  expired: {
    title: 'Expired',
    tooltip: 'The link is expired.',
    // TODO: Darker text
    // TODO: background "pastel red"
    // color: theme.legacy.red.light,
    color: 'red'
  },
  pending: {
    title: 'Pending',
    tooltip: 'The test-taker has not used this link.',
    // color: theme.gauge.yellow
    color: 'processing'
  },
  cancelled: {
    title: 'Deactivated',
    tooltip: 'The test-taker has not used this link.',
    // TODO: Darker
    // color: theme.primary
    color: 'default'
  }
};

export function DisplayAssessmentsTableContent(props: {
  assessments: AssessmentIndexEntry[];
}) {
  const errorList = useErrorListContext();
  const assessmentLinkMutationActions = useAssessmentAPI();
  const patientInfo = usePatientInfoContext();

  const dataSource = useMemo(
    () =>
      sortBy(
        props.assessments.map(_ => ({
          ..._,
          ..._.metadata
        })), // Sort by the creation time
        _ => -(_.metadata?.created.unix() ?? 0)
      ),
    [props.assessments]
  );

  return (
    <Table
      dataSource={dataSource}
      columns={[
        {
          dataIndex: 'created',
          title: 'Created at',
          render(value: Moment) {
            return formatDisplayDateTime(value);
          }
        },
        // {
        //   dataIndex: 'expiresAt',
        //   title: 'Expires at',
        //   render(value: Moment) {
        //     const formattedDate = formatDisplayDateTime(value);

        //     const fromNow = differenceInCalendarDays(
        //       value.toDate(),
        //       Date.now()
        //     );
        //     if (fromNow === 0) {
        //       return 'Today';
        //     }
        //     if (fromNow < 0) {
        //       return `${formattedDate}`;
        //     }

        //     return (
        //       <Tooltip title={`In ${fromNow} days`}>{formattedDate}</Tooltip>
        //     );
        //   }
        // },
        {
          dataIndex: 'modifiedAt',
          title: 'Last updated at',
          render(_value: Moment, record) {
            const updatedDate = record.metadata?.updated;
            if (updatedDate === undefined) {
              return 'unknown';
            }
            return formatDisplayDateTime(updatedDate);
          }
        },
        {
          dataIndex: 'status',
          title: 'Status',
          render(_value, record) {
            const val = STATUS_TO_TAG_MAP[record.status];
            return (
              <Tooltip title={val.tooltip}>
                <Tag color={val.color}>{val.title}</Tag>
              </Tooltip>
            );
          }
        },
        {
          title: '',
          render(_value: unknown, record) {
            const menuItems: MenuProps['items'] = [
              {
                key: 'deactivate',
                label: 'Deactivate',
                icon: <CloseCircleOutlined />,
                disabled: !['pending', 'started'].includes(record.status),
                onClick: () => {
                  errorList.errorList.removeError('deactivation', {
                    ignoreMissing: true
                  });
                  assessmentLinkMutationActions.cancel.mutate(
                    {
                      assessmentId: record.id,
                      patientId: patientInfo.patientInfo.patient.id
                    },
                    {
                      onError(e) {
                        const error = resolveError(e);
                        errorList.errorList.addError(error, 'deactivation');
                      }
                    }
                  );
                }
              }
            ];
            assessmentLinkMutationActions.cancel.variables?.assessmentId ===
              record.id;
            return (
              <div>
                {assessmentLinkMutationActions.cancel.isPending &&
                assessmentLinkMutationActions.cancel.variables?.assessmentId ===
                  record.id ? (
                  <Spin />
                ) : (
                  <Dropdown
                    menu={{ items: menuItems }}
                    trigger={['click']}
                    arrow
                    disabled={
                      assessmentLinkMutationActions.cancel.isPending &&
                      assessmentLinkMutationActions.cancel.variables
                        ?.assessmentId === record.id
                    }
                  >
                    <Tooltip title={'Extra options'}>
                      {/* <BaseButton> */}
                      <EllipsisOutlined />
                      {/* </BaseButton> */}
                    </Tooltip>
                  </Dropdown>
                )}
              </div>
            );
          }
        }
      ]}
      rowKey={'id'}
      pagination={{
        pageSize: 3
      }}
    />
  );
}
